/**
 * Environment variable accessor — works in browser (Vite) AND Node (worker).
 *
 * Browser: reads import.meta.env.VITE_* (Vite injects at build time).
 * Node: reads process.env.X (no VITE_ prefix), with fallback to process.env.VITE_X.
 *
 * Usage everywhere:
 *   import { env } from "@/lib/env";
 *   const key = env("VITE_FAL_AI_API_KEY");
 *
 * Replace ALL `import.meta.env.VITE_X` with `env("VITE_X")` so the same source
 * file compiles for both runtimes.
 */

type EnvKey =
  | "VITE_FAL_AI_API_KEY"
  | "VITE_DATAFORSEO_LOGIN"
  | "VITE_DATAFORSEO_PASSWORD"
  | "VITE_SUPABASE_URL"
  | "VITE_SUPABASE_ANON_KEY"
  | "VITE_LOG_LEVEL"
  | "VITE_CRAWL4AI_URL"
  | "VITE_HEADER_PROPOSALS_API_KEY"
  | "VITE_HEADER_PROPOSALS_APP_ID"
  | "VITE_OPENAI_API_KEY"
  | "VITE_ANTHROPIC_API_KEY"
  | "VITE_GEONODE_PROXY_SERVER"
  | "VITE_GEONODE_PROXY_USERNAME"
  | "VITE_GEONODE_PROXY_PASSWORD";

const isBrowser = typeof window !== "undefined";

export function env(key: EnvKey): string | undefined {
  if (isBrowser) {
    // Vite-injected. (import.meta as any).env to skip TS strictness.
    return (import.meta as any).env?.[key];
  }
  // Node (worker): try process.env.X first (without VITE_), then full key as fallback.
  const stripped = key.replace(/^VITE_/, "");
  return process.env[stripped] ?? process.env[key];
}

export function isNodeRuntime(): boolean {
  return !isBrowser;
}
